import { Link } from "react-router-dom";
import useSWR from "swr";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { fetchStats } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Reveal, StaggerGroup, riseItem } from "../components/Reveal";
import Counter from "../components/Counter";
import { CONTACT, BUYER_PROMISE, HABITS } from "../lib/brand";

export default function About() {
  const { data: stats } = useSWR("stats", fetchStats);
  const metrics = [
    { value: stats?.total_varieties ?? 220, suffix: "+", label: "Varieties tracked" },
    { value: stats?.total_families ?? 13, suffix: "+", label: "Fruit families" },
    { value: stats?.indian_states_count ?? 19, suffix: "+", label: "Indian growing states" },
    { value: 2, suffix: "", label: "Generations of trade" },
  ];

  return (
    <>
      <PageHeader
        overline="About us"
        lines={["Two generations of", "trade, under one roof."]}
      />

      <section className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-32">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <Reveal>
            <p className="font-display text-2xl font-medium leading-snug text-forest md:text-4xl">
              Venya Trade Enterprises, a subsidiary of {CONTACT.parent}, has been a trusted
              name in fruit wholesale for two generations.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="space-y-6 text-forest-light">
            <p>
              We source and supply fresh Indian, exotic, and imported fruits — connecting
              quality orchards worldwide with retailers, distributors, and businesses who
              demand the best.
            </p>
            <p>
              At Venya, we believe good health starts with good fruit. Our mission is to make
              India healthier by delivering quality you can rely on, giving people the power
              to experience healthy living.
            </p>
            <p>
              Every fruit is rigorously checked, reliably sourced, and delivered on time.
              Built on decades of customer trust, we take pride in offering premium produce at
              competitive prices — with our Mangoes and Apples standing out as customer
              favourites. At Venya, freshness isn't a promise —{" "}
              <span className="italic text-forest">it's a legacy.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* metrics */}
      <section className="border-y border-border bg-cream-200">
        <div className="mx-auto grid max-w-[1400px] grid-cols-2 gap-px overflow-hidden bg-border px-0 lg:grid-cols-4">
          {metrics.map((m) => (
            <div key={m.label} className="bg-cream-200 p-8 md:p-12">
              <div className="font-display text-5xl font-semibold text-forest md:text-6xl">
                <Counter value={m.value} suffix={m.suffix} />
              </div>
              <div className="mt-3 font-sans text-sm text-forest-muted">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* buyer promise */}
      <section className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
        <Reveal className="mb-12 max-w-3xl">
          <h2 className="font-display text-3xl font-medium leading-[1.05] text-forest md:text-5xl">
            One commission desk. Multiple fruit families. Two generations of relationships.
          </h2>
        </Reveal>
        <StaggerGroup className="grid gap-px overflow-hidden rounded-sm border border-border bg-border md:grid-cols-2">
          {BUYER_PROMISE.map((p, i) => (
            <motion.div key={i} variants={riseItem} className="flex gap-5 bg-cream-100 p-8 md:p-10">
              <span className="font-mono text-sm text-gold">0{i + 1}</span>
              <p className="text-lg leading-relaxed text-forest">{p}</p>
            </motion.div>
          ))}
        </StaggerGroup>
      </section>

      {/* habits */}
      <section className="bg-forest text-cream-100">
        <div className="mx-auto max-w-[1400px] px-5 py-24 md:px-10 md:py-32">
          <Reveal className="mb-14">
            <h2 className="max-w-3xl font-display text-3xl font-medium leading-[1.05] md:text-5xl">
              Freshness isn't a promise here — <span className="italic text-gold">it's a habit.</span>
            </h2>
          </Reveal>
          <StaggerGroup className="grid gap-px overflow-hidden border border-cream-100/15 bg-cream-100/15 md:grid-cols-2 lg:grid-cols-4">
            {HABITS.map((h) => (
              <motion.div key={h.tag} variants={riseItem} className="bg-forest p-8">
                <h3 className="mb-3 font-display text-2xl text-cream-100">{h.title}</h3>
                <p className="text-sm leading-relaxed text-cream-100/70">{h.body}</p>
              </motion.div>
            ))}
          </StaggerGroup>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              className="group mt-14 inline-flex items-center gap-3 rounded-full border border-gold px-7 py-4 text-gold transition-colors duration-300 hover:bg-gold hover:text-forest"
            >
              <span className="tracking-wide" style={{ fontSize: "0.8rem" }}>Work with the desk</span>
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
